import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { idleText, idleMoney } from '@evi/shared';
import { renderInline } from '@evi/ui';
import { idleClockLabel, useIdleClock } from './charging/IdlePanel';
import { useTheme } from '@evi/skins';
import { ChargingAnimation } from '@evi/ui';
import { useActiveSession } from '@/hooks/useActiveSession';
import { fetchActiveSkin, formatCents, type SessionData } from '@/lib/api';
import styles from './MiniChargingBar.module.css';

/**
 * Persistent floating bar that summarises the driver's active charging
 * session while they navigate elsewhere in the PWA. Sits 10px above
 * the floating pill nav (z-index 49 to the pill's 50 — pill always
 * wins on overlap).
 *
 * Self-hides when:
 *   - no active session (most common case)
 *   - the user is currently on /charging/:id (avoid duplicating the
 *     full ring on the page that already owns it)
 *   - a session just transitioned to a terminal state (briefly flash
 *     a success styling, then unmount when the polling refetch
 *     returns null)
 *
 * SOC + power are derived from `meter_values.at(-1)` the same way
 * the full ChargingPage does — keeps the two visualisations in
 * lock-step without doubling server-side derivation.
 */

interface MiniChargingBarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

// Battery capacity assumption (kWh) — matches the heuristic used by
// the full ChargingPage. Pre-charge profile work supersedes this in
// a follow-up ticket.
const BATTERY_KWH = 60;

function deriveMetrics(s: SessionData) {
  const last = s.meter_values?.[s.meter_values.length - 1];
  // SOC: prefer the meter's own soc_percent if reported; otherwise
  // estimate from delivered kWh against the assumed pack size. Capped
  // at 99.9 so we never render "100%" while still actively charging
  // (StopTransaction triggers the 100% transition via the completion
  // success path).
  const soc = typeof last?.soc_percent === 'number'
    ? Math.max(0, Math.min(99.9, last.soc_percent))
    : Math.min(99.9, Math.max(0, (s.energy_kwh / BATTERY_KWH) * 100));
  const powerKw = last?.power_kw ?? 0;
  const costCents = s.idle_view?.total_cents ?? s.cost?.total ?? 0;
  return { soc, powerKw, costCents };
}

export function MiniChargingBar({ currentPath, onNavigate }: MiniChargingBarProps) {
  const { data: session } = useActiveSession();
  const { theme, mode } = useTheme();
  // Skin-aware chip: if a skin is applied, the mini chip's animation
  // should match the skin. Otherwise fall through to the family id.
  const skinQ = useQuery({
    queryKey: ['unlocks', 'active-skin'],
    queryFn: fetchActiveSkin,
    staleTime: 60_000,
  });
  const themeCode = skinQ.data?.skin?.code ?? theme.id;
  // Hold onto the last-seen session so we can play a brief "completed"
  // flash when the polling refetch returns null after we previously
  // had an active session.
  const previousSessionRef = useRef<SessionData | null>(null);
  const [completionFlash, setCompletionFlash] = useState<SessionData | null>(null);

  useEffect(() => {
    if (session) {
      // Only a session that actually ran earns the "Session complete"
      // flash. A pending shell that vanishes (cancelled, swept, refused)
      // never delivered anything — flashing "0.00 kWh · 100%" for it
      // would announce a completion that did not happen.
      previousSessionRef.current = session.phase === 'pending' ? null : session;
      setCompletionFlash(null);
      return;
    }
    if (previousSessionRef.current && !completionFlash) {
      // Session just disappeared — flash success. The 1.6s dismiss timer
      // lives in the effect below (keyed on completionFlash alone) so that
      // setting completionFlash here doesn't re-run THIS effect and clear
      // the timer before it can fire, which left the bar stuck on screen.
      setCompletionFlash(previousSessionRef.current);
      previousSessionRef.current = null;
    }
  }, [session, completionFlash]);

  // Auto-dismiss the completion flash 1.6s after it appears.
  useEffect(() => {
    if (!completionFlash) return undefined;
    const t = window.setTimeout(() => setCompletionFlash(null), 1600);
    return () => window.clearTimeout(t);
  }, [completionFlash]);

  // Render rules.
  if (currentPath.startsWith('/charging/')) return null;
  // The home screen owns the live session while one exists (HomeRoute
  // renders ChargingPage at '/'), so the bar would double the ring and
  // — via --evi-live-session-bar-offset — lift the page's controls by
  // 72px on top of it. Same rationale as /charging/:id.
  if ((currentPath === '/' || currentPath === '/charging') && session?.phase !== 'finishing') return null;
  // The pre-session page owns the start it just dispatched: the pending
  // shell exists server-side (and reaches this cache over SSE) seconds
  // before the RemoteStart round-trip answers, and a "Waiting for
  // charger" bar sliding in under the "Sending…" CTA — lifting it 72px —
  // is exactly the jump the driver should not see there.
  if (
    session?.phase === 'pending'
    && (currentPath.startsWith('/stations/') || currentPath.startsWith('/charging-station/'))
  ) return null;
  // Session-complete page: same rationale as /charging/:id — the page
  // already owns the session summary, and the bar's fixed band
  // (bottom ~82px, z 49) sits exactly on the page's StickyCTA Done
  // button (bottom ~88px, z 10). While the active-session poll lags
  // behind the stop, the bar is a full-width button COVERING Done:
  // taps either bounce back to /charging/:id or, during the 1.6s
  // completion flash, do nothing at all. Route shape mirrors App.tsx's
  // completeMatch.
  if (/^\/sessions\/[^/]+\/complete$/.test(currentPath)) return null;
  if (!session && !completionFlash) return null;

  const display = session ?? completionFlash;
  if (!display) return null;
  const isFlash = !session && Boolean(completionFlash);
  const m = deriveMetrics(display);

  // Visible. Owns the --evi-live-session-bar-offset CSS var
  // while mounted so other bottom-anchored surfaces (BottomSheet,
  // any future overlays) can pad themselves to clear the bar
  // without coupling to MiniChargingBar's exact dimensions or
  // re-deriving them via ResizeObserver. 72px = 62px bar height
  // + 10px gap above the pill (the inline 'bottom' rule on the
  // .bar in MiniChargingBar.module.css). The variable is unset
  // when the bar is hidden so consumers naturally fall back to
  // their normal pad via `var(--evi-live-session-bar-offset, 0px)`.
  return <BarVisible
    display={display}
    isFlash={isFlash}
    metrics={m}
    themeCode={themeCode}
    mode={mode}
    onNavigate={onNavigate}
  />;
}

/** Visible-render branch — extracted so the variable side-effect
 *  scope is unambiguous. The hook only mounts when the bar is
 *  actually rendered, and unmounts (clearing the var) the
 *  moment the parent's render rules drop the bar. */
function BarVisible({
  display, isFlash, metrics: m, themeCode, mode, onNavigate,
}: {
  display: SessionData;
  isFlash: boolean;
  metrics: { soc: number; powerKw: number; costCents: number };
  themeCode: string;
  mode: 'light' | 'dark';
  onNavigate: (path: string) => void;
}) {
  const { i18n } = useTranslation();
  const idleClock = useIdleClock(display.idle_view);
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--evi-live-session-bar-offset',
      '72px',
    );
    return () => {
      document.documentElement.style.removeProperty('--evi-live-session-bar-offset');
    };
  }, []);

  // Phase signal from /sessions/active. Defaults to 'active' when
  // absent (legacy responses, demo paths) so the existing rendering
  // is preserved by default. The completion flash branch
  // (post-unplug, 1.6s post-mount) takes precedence over phase
  // because completionFlash and the live-session pull are
  // mutually-exclusive states from the parent's render rules.
  const phase: 'pending' | 'active' | 'finishing' = display.phase ?? 'active';
  const isFinishing = !isFlash && phase === 'finishing';
  // Pending = the plug-in window after the Start tap, before the
  // charger's StartTransaction. Nothing is flowing and there is no
  // SOC or cost to show; the bar reads as a quiet "waiting" strip
  // that takes the driver back to the live view.
  const isPending = !isFlash && phase === 'pending';

  // Aria label per render mode. Three branches:
  //   - completion flash (post-unplug brief celebratory state)
  //   - finishing (plug still attached, billing finalized)
  //   - active (live charging)
  const ariaLabel = isFlash
    ? 'Charging session complete'
    : isPending
      ? `Waiting for the charger at ${display.location_name ?? 'station'} to start. Tap to view.`
    : isFinishing
      ? `Session ending: plug still attached at ${display.location_name ?? 'station'}. ${display.energy_kwh.toFixed(2)} kilowatt hours delivered. Tap to view.`
      : `Active charging session: ${m.soc.toFixed(1)}% at ${m.powerKw.toFixed(0)} kilowatts. Tap to view.`;

  return (
    <button
      type="button"
      className={`${styles.bar} ${isFlash ? styles.barComplete : ''}`}
      // `data-phase` exposes the lifecycle state to CSS without
      // adding a class-name combinatorial. Rules in the .module.css
      // freeze the wavy fill animation and the pulsing edge during
      // 'finishing' so the bar reads quiet-but-themed rather than
      // suggesting active power flow that isn't happening.
      data-phase={phase}
      aria-label={ariaLabel}
      onClick={() => {
        if (isFlash) return;
        // Home owns the live session (pending, active or finishing).
        onNavigate(isFinishing ? `/sessions/${display._id}/complete` : '/');
      }}
    >
      {/* Progress fill that grows under the content as SOC climbs.
          Hosts a child wavy overlay (.fillWave) that paints a
          themed sine wave on top of the ambient gradient at the top
          of the bar. The vertical opacity fade inside the wave's
          mask keeps text contrast intact in the middle/bottom of
          the bar where readouts sit. */}
      <span
        className={styles.fill}
        aria-hidden
        style={{ width: `${isPending ? 0 : Math.max(0, Math.min(100, m.soc))}%` }}
      >
        <span className={styles.fillWave} aria-hidden />
      </span>
      {/* Pulsing edge at the boundary. */}
      <span
        className={styles.edge}
        aria-hidden
        style={{ left: `${isPending ? 0 : Math.max(0, Math.min(100, m.soc))}%` }}
      />

      {/*
        Skin-aware mini chip — replaces the legacy bolt icon. Renders
        the active theme's charging animation at 44px so the driver
        sees their chosen look mirrored between the full screen and
        the persistent bar. `glow="none"` because the bar already owns
        its bloom; doubling them would read as noisy.

        `active={false}` during finishing so the chip's own animation
        quiets down — matches the bar-level freeze cue without
        coupling MiniChargingBar's CSS to ChargingAnimation's
        internal animation triggers.
      */}
      <span className={styles.iconChip} aria-hidden>
        <ChargingAnimation
          themeCode={themeCode}
          mode={mode}
          percent={isFlash ? 100 : isPending ? 0 : m.soc}
          active={!isFlash && !isFinishing && !isPending}
          size={44}
          glow="none"
        />
      </span>

      <div className={styles.text}>
        <div className={styles.title}>
          {isFlash ? 'Session complete' : (display.location_name ?? 'Charging')}
        </div>
        <div className={styles.sub}>
          {isFlash
            ? `${display.energy_kwh.toFixed(2)} kWh delivered`
            : isPending
              ? 'Waiting for charger — plug in now'
            : isFinishing
              // Power Flow row would read "Stall O1 · 0 kW" during
              // finishing — meaningless and misleading. Replaced
              // with a quiet status line; copy is placeholder pending
              // UX iteration.
              ? display.idle_view?.charging_ended_at && !display.idle_view.billing_closed
                ? renderInline(idleText(i18n.language, idleClock.left > 0 ? 'barGrace' : 'barBilling', {
                  time: idleClockLabel(idleClock.left > 0 ? idleClock.left : idleClock.elapsed),
                  amount: idleMoney(display.idle_view.idle_cents, display.cost.currency, i18n.language) }))
                : idleText(i18n.language, 'barWaiting')
              : `Stall ${String(display.connector_id).padStart(2, '0')} · ${m.powerKw.toFixed(0)} kW`}
        </div>
      </div>

      <div className={styles.right}>
        <div className={styles.pct}>
          {/* During finishing the percentage slot becomes the final
              kWh readout — battery SOC as a percentage is no longer
              the relevant stat post-billing; total energy delivered
              is. Cost slot below stays unchanged. */}
          {isFlash
            ? '100%'
            : isPending
              ? '…'
            : isFinishing
              ? `${display.energy_kwh.toFixed(2)} kWh`
              : `${m.soc.toFixed(1)}%`}
        </div>
        {/* In the session's own currency (item 3): CA$ at a Canadian home. */}
        {!isPending && <div className={styles.cost}>{formatCents(m.costCents, display.cost?.currency)}</div>}
      </div>
    </button>
  );
}
